// @flow
// See TripModalRoute for story
/* eslint-disable ta-code-conventions/has-matching-storydoc */
import * as React from 'react';
import { connect } from 'react-redux';
import { ErrorBoundary } from '@ta/common.error-handling';
import Logging from '@ta/common.logging';
import * as Import from '@ta/platform.import';
import { featureIsEnabled } from '@ta/common.features';
import { TripDetailSkeletonLoader, TripModalViewLayout } from '@ta/trips.trip-detail-layout';
import { type FullTripQuery, GetFullTrip } from '@ta/trips.graphql';
import { isTripUser } from '@ta/trips.trip-util';
import type { TripModalNavigator } from '@ta/trips.trip-types';
import { useInvalidatableQuery } from '@ta/platform.urql';
import type { CombinedError } from 'urql';

type ExternalProps = {|
  /** Minimum set of arguments needed to render a TripModalView */
  args: TripModalNavigator,
  /** An optional callback that's fired in the event the modal is supposed to close */
  onClose?: () => mixed,
|};

type ConnectedProps = {|
  ...ExternalProps,
  // The header blocks the mobile map because of rendering bugs on iOS Safari
  isIOSSafari: boolean,
  loggedInUserId: string | null,
|};

type Props = {|
  ...ConnectedProps,
  loading: boolean,
  data: FullTripQuery,
  error: CombinedError,
|};

const TripModalContent = Import.component.onUsage(
  () => import('@ta/trips.trip-modal'),
  'TripModalContent',
  <TripDetailSkeletonLoader />,
);

const DeferredTripModalView = ({
  loggedInUserId,
  args: { title, scrollToReference },
  onClose,
  loading,
  data,
  error,
  isIOSSafari,
}: Props) => {
  const trip = data && data.tripsGetOne;
  const isUser = trip && isTripUser((trip: any), loggedInUserId);

  const handleExceptionalState = React.useCallback(() => {
    // Current behavior is that fatal errors that have no way to recover should refresh the page
    // eslint-disable-next-line no-restricted-globals
    window.location.reload(true);
  }, []);

  React.useEffect(() => {
    // If a user of a trip somehow was able to render the view (e.g. logging in from a social action), treat that as an exceptional case
    if (isUser) {
      handleExceptionalState();
    }
  }, [isUser, handleExceptionalState]);

  React.useEffect(() => {
    if (!loading && !trip && error) {
      Logging.error('A service error occurred in the TripModal query', error);
      // If there was a fatal error and we can't show the modal, do a hard refresh to Trips page
      handleExceptionalState();
    }
  }, [loading, trip, error, handleExceptionalState]);

  const [suppressTitleBar, setSuppressTitleBar] = React.useState(false);

  return (
    <TripModalViewLayout title={!suppressTitleBar && title} onClose={onClose}>
      <ErrorBoundary
        onError={handleExceptionalState}
        errorRenderer={() => <TripDetailSkeletonLoader />}
      >
        {/* // Viewing your own/collaborated trip in a modal isn't supported yet. Links to a Trip should have redirected to the Trip detail page
             In the exceptional cases where it doesn't, show the loading state */}
        {trip && !loading && !isUser ? (
          <TripModalContent
            trip={trip}
            scrollToReference={scrollToReference}
            onMobileMapMaximized={isIOSSafari ? setSuppressTitleBar : undefined}
          />
        ) : (
          <TripDetailSkeletonLoader />
        )}
      </ErrorBoundary>
    </TripModalViewLayout>
  );
};

const DeferredTripModalViewWithQuery = (props: ConnectedProps) => {
  const tripId = props.args.id;

  const validateQuery = ({ tripId: _tripId }) =>
    _tripId === tripId ? 'invalidateAndRefetch' : 'validate';

  const [tripsQuery] = useInvalidatableQuery(
    {
      query: GetFullTrip,
      variables: {
        tripId,
        socialReference: featureIsEnabled('social_at_referencing_profile_links'),
      },
    },
    {
      addItemToTrip: validateQuery,
      tripsRemoveItem: validateQuery,
      tripsRemoveReference: validateQuery,
      updateTrip: validateQuery,
      organizeTrip: validateQuery,
      // these are trip items dependencies, perhaps we should split the query and load trip item data separately
      addTripItemComment: validateQuery,
      updateTripComment: validateQuery,
      tripsCommentsRemoveOne: validateQuery,
    },
  );

  return (
    <DeferredTripModalView
      {...props}
      loading={tripsQuery.fetching}
      data={tripsQuery.data}
      error={tripsQuery.error}
    />
  );
};

export default connect<ConnectedProps, ExternalProps, _, _, _, _>(state => ({
  loggedInUserId: state.auth.loggedInUserId,
  isIOSSafari:
    state.meta.device.browser.family === 'SAFARI' && state.meta.device.os.family === 'IOS',
}))(DeferredTripModalViewWithQuery);
